import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { fetchWorkout, fetchWorkoutRecipes } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ArrowLeft, Check, Star, Flame, Timer, Plus, ChefHat, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const WorkoutPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [workout, setWorkout] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tierId, setTierId] = useState("monthly");
    const { addItem } = useCart();

    useEffect(() => {
        Promise.all([fetchWorkout(id), fetchWorkoutRecipes(id)])
            .then(([w, r]) => {
                setWorkout(w);
                setRecipes(r);
                if (w?.tiers?.length) {
                    setTierId(w.tiers.find((t) => t.id === "monthly")?.id || w.tiers[0].id);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="h-[100dvh] flex items-center justify-center bg-[#0A0A0A] text-white/60 text-sm">Loading…</div>;
    }
    if (!workout) {
        return <div className="h-[100dvh] flex items-center justify-center bg-[#0A0A0A] text-white/60 text-sm">Not found.</div>;
    }

    const selectedTier = workout.tiers.find((t) => t.id === tierId) || workout.tiers[0];

    return (
        <div data-testid="workout-page" className="min-h-[100dvh] bg-[#0A0A0A] pb-64">
            <div className="relative h-[55dvh]">
                <img
                    src={workout.image_url}
                    alt={workout.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-[#0A0A0A]/40" />
                <button
                    data-testid="back-button"
                    onClick={() => navigate(-1)}
                    className="absolute top-5 left-4 w-10 h-10 rounded-full bg-black/50 border border-white/10 backdrop-blur flex items-center justify-center"
                >
                    <ArrowLeft size={18} />
                </button>
                <span className="absolute top-5 right-4 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#E1FF01] text-[#0A0A0A]">
                    Subscription
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <img
                            src={workout.creator_avatar}
                            alt={workout.creator}
                            className="w-8 h-8 rounded-full object-cover border border-white/30"
                        />
                        <span className="text-sm font-semibold">{workout.creator}</span>
                        <span className="text-xs text-white/50 flex items-center gap-1 ml-auto">
                            <Star size={11} className="fill-[#E1FF01] text-[#E1FF01]" />
                            {workout.rating} ({workout.reviews_count})
                        </span>
                    </div>
                    <h1 className="font-display text-5xl uppercase leading-[0.95]">{workout.title}</h1>
                </div>
            </div>

            <div className="px-5 pt-6 space-y-7">
                <div className="flex flex-wrap gap-2">
                    {workout.duration_min && (
                        <span className="text-xs px-3 py-1.5 rounded-full bg-[#141414] border border-neutral-800 flex items-center gap-1">
                            <Timer size={12} /> {workout.duration_min} min / day
                        </span>
                    )}
                    {workout.intensity && (
                        <span className="text-xs px-3 py-1.5 rounded-full bg-[#141414] border border-neutral-800 flex items-center gap-1">
                            <Flame size={12} className="text-[#E1FF01]" /> {workout.intensity}
                        </span>
                    )}
                    {workout.tags?.map((t) => (
                        <span
                            key={t}
                            className="text-xs px-3 py-1.5 rounded-full bg-[#141414] border border-neutral-800"
                        >
                            #{t}
                        </span>
                    ))}
                </div>

                <p className="text-sm text-white/80 leading-relaxed">{workout.description}</p>

                {/* Tiers */}
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-3">Pick your plan</p>
                    <div className="grid gap-2.5">
                        {workout.tiers.map((t) => {
                            const active = tierId === t.id;
                            return (
                                <button
                                    key={t.id}
                                    data-testid={`tier-${t.id}`}
                                    onClick={() => setTierId(t.id)}
                                    className={`text-left rounded-xl border px-4 py-3.5 transition-all ${
                                        active
                                            ? "border-[#E1FF01] bg-[#E1FF01]/5"
                                            : "border-neutral-800 bg-[#141414] hover:border-white/30"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`w-4 h-4 rounded-full border-2 ${
                                                    active ? "border-[#E1FF01] bg-[#E1FF01]" : "border-white/40"
                                                }`}
                                            />
                                            <span className="font-display text-xl uppercase">{t.name}</span>
                                            {t.badge && (
                                                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E1FF01] text-[#0A0A0A] font-bold">
                                                    {t.badge}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <p className="font-display text-2xl">${t.price}</p>
                                            <p className="text-[10px] uppercase tracking-widest text-white/50">
                                                / {t.period}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-3">What's included</p>
                    <ul className="space-y-2">
                        {workout.includes?.map((line) => (
                            <li key={line} className="flex items-start gap-2 text-sm">
                                <Check size={16} className="text-[#E1FF01] mt-0.5 shrink-0" strokeWidth={2.6} />
                                <span className="text-white/85">{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Recipe suggestions */}
                {recipes.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] flex items-center gap-1.5">
                                <ChefHat size={12} /> Recipe suggestions
                            </p>
                            <span className="text-[10px] uppercase tracking-widest text-white/40">
                                Free with subscription
                            </span>
                        </div>
                        <div className="space-y-2.5">
                            {recipes.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/recipe/${r.id}`}
                                    data-testid={`recipe-link-${r.id}`}
                                    className="flex items-center gap-3 bg-[#141414] border border-neutral-800 hover:border-[#E1FF01]/40 rounded-xl p-2.5 transition-all"
                                >
                                    <img
                                        src={r.image_url}
                                        alt={r.title}
                                        className="w-16 h-16 object-cover rounded-lg"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-display text-base uppercase leading-tight line-clamp-1">
                                            {r.title}
                                        </p>
                                        <p className="text-xs text-white/55 line-clamp-1 mt-0.5">{r.summary}</p>
                                        <div className="flex gap-2 mt-1.5">
                                            <span className="text-[10px] text-white/70">
                                                {r.calories} kcal
                                            </span>
                                            <span className="text-[10px] text-white/70">
                                                P {r.protein_g}g
                                            </span>
                                            <span className="text-[10px] text-white/70 flex items-center gap-0.5">
                                                <Timer size={9} /> {r.prep_min}m
                                            </span>
                                        </div>
                                    </div>
                                    <ChevronRight size={16} className="text-white/40" />
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Sticky bottom CTA */}
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-[480px] px-5 z-30">
                <button
                    data-testid="workout-subscribe"
                    onClick={() => {
                        addItem(workout, selectedTier);
                        toast.success(`${workout.title} — ${selectedTier.name} added`);
                    }}
                    className="w-full bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] font-display text-2xl uppercase tracking-wider py-4 rounded-md flex items-center justify-center gap-2 transition active:scale-[0.98] shadow-[0_8px_24px_rgba(225,255,1,0.25)]"
                >
                    <Plus size={22} strokeWidth={3} />
                    Subscribe ${selectedTier.price}/{selectedTier.period}
                </button>
            </div>
        </div>
    );
};

export default WorkoutPage;
